import { Button, Stack, useTheme } from '@mui/material';
import { GridApi, SelectionChangedEvent } from 'ag-grid-community';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useMemo, useState } from 'react';

import { AgTable } from '../../../../components/AgTable/AgTable';
import { ExportGridButton } from '../../../../components/AgTable/ExportGridButton';
import { useExtendedExportSettings } from '../../../../components/AgTable/exportToExcelDefs';
import { multiRowSelectionDefs } from '../../../../components/AgTable/rowSelectionDefs';
import { FadeInGridWrapper } from '../../../../components/grid-renderers/FadeInGridWrapper';
import { useLoadingBarState } from '../../../../components/LoadingBar/LoadingBarContext';
import { ICompanyDataModel } from '../../../../data-models/company.data-model';
import { IKPIRequestDataModel, KPIRequestStatus } from '../../../../data-models/kpi-requests.data-model';
import { filterKPIRequestsByStatusesJ } from '../../../../services/state/KPI/KPIRequestsStateJ';
import { portcosByIdMapAtomJ } from '../../../../services/state/PortCosStateJ';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { ExportButtonContainer } from './components/KpiStyleDefs';
import { useKPIRequestsNotSentActions } from './hooks/useKPIRequestsNotSentActions';
import useKPIRequestsSentColDefs from './hooks/useKPIRequestsSentColDefs';
import { RequestConfirmDialog } from './KPIRequestResponse/RequestConfirmDialog';
import { ActionButtonStack, ActionButtonWrapper } from '../../../../components/AgTable/gridActionBar';

export function SentKpiRequestsGrid({
  status,
}: {
  status: typeof KPIRequestStatus.Sent | typeof KPIRequestStatus.Archived;
}) {
  const { allowMultipleSendReminders } = useFlags<MaggieFeatureFlags>();
  const requests = useAtomValue(filterKPIRequestsByStatusesJ([status])) ?? null;
  const { columnDefs, defaultColDef } = useKPIRequestsSentColDefs(status);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [selectedRows, setSelectedRows] = useState<IKPIRequestDataModel[] | null>(null);
  const [showSentModal, setShowSentModal] = useState(false);
  const portcosById = useAtomValue(portcosByIdMapAtomJ);
  const { sendKPIRequestAction } = useKPIRequestsNotSentActions();
  const {
    state: { isLoading: isSending },
  } = useLoadingBarState();

  const selectedRequestCompanies = useMemo(() => {
    const selectedCompanies = new Map<number, ICompanyDataModel>();
    selectedRows?.forEach((row) => {
      const company = portcosById.get(row.companyId ?? -1);
      if (company && !selectedCompanies.has(company?.id)) {
        selectedCompanies.set(company.id, company);
      }
    });

    return selectedCompanies;
  }, [portcosById, selectedRows]);

  const onGridReady = (params: { api: GridApi }) => {
    setGridApi(params.api);
  };

  const { exportParams } = useExtendedExportSettings(
    new Set(['companyId', 'templateUuid']),
    `${status}-kpi-requests`
  );

  const handleExport = useCallback(() => {
    gridApi?.exportDataAsExcel();
  }, [gridApi]);

  const handleSelectionChanged = useCallback(
    (e: SelectionChangedEvent) => {
      const selectedRows = e.api.getSelectedRows();
      setSelectedRows(selectedRows);
    },
    [setSelectedRows]
  );

  const handleOnSendAll = useCallback(() => {
    setShowSentModal(true);
  }, []);

  const handleOnCancelAll = () => {
    gridApi?.deselectAll();
  };

  const handleOnConfirmSendAll = useCallback(async () => {
    if (!gridApi || !selectedRows) {
      return;
    }
    const selectedRowsIds = selectedRows.map((row) => row.id);

    const payload = {
      requestsIds: selectedRowsIds as number[],
      sendReminder: true,
    };

    await sendKPIRequestAction(payload);
    setShowSentModal(false);

    gridApi?.deselectAll();
    setSelectedRows(null);
  }, [gridApi, selectedRows, sendKPIRequestAction]);

  return (
    <Stack height={'100%'}>
      {showSentModal && (
        <RequestConfirmDialog
          open={showSentModal}
          onClose={() => setShowSentModal(false)}
          loading={isSending}
          onConfirm={handleOnConfirmSendAll}
          requests={selectedRows ?? []}
          message={` Selected ${selectedRequestCompanies.size} companies:`}
          title='Send additional Reminder(s)'
        />
      )}
      <div css={ExportButtonContainer}>
        <ExportGridButton onClick={handleExport} />
      </div>
      <FadeInGridWrapper key={status}>
        <ActionButtons selectedRows={selectedRows} actions={[handleOnSendAll, handleOnCancelAll]} />
        <AgTable
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          groupDefaultExpanded={-1}
          onGridReady={onGridReady}
          rowData={requests}
          rowGroupPanelShow='always'
          suppressMovableColumns={true}
          suppressRowDrag={true}
          onSelectionChanged={handleSelectionChanged}
          defaultExcelExportParams={exportParams}
          defaultCsvExportParams={exportParams}
          rowSelection={allowMultipleSendReminders ? multiRowSelectionDefs : undefined}
        />
      </FadeInGridWrapper>
    </Stack>
  );
}

interface ActionButtonsProps {
  selectedRows: IKPIRequestDataModel[] | null;
  actions: (() => void)[];
}

const ActionButtons = memo(function ActionButtons({ actions, selectedRows }: ActionButtonsProps) {
  const { colors } = useTheme();
  const {
    state: { isLoading },
  } = useLoadingBarState();
  const [handleOnSendAll, handleOnCancelAll] = actions;

  return (
    <ActionButtonWrapper>
      {selectedRows?.length ? (
        <ActionButtonStack direction='row'>
          <Button disabled={isLoading} variant='contained' color='secondary' onClick={handleOnSendAll}>
            Send Selected {selectedRows?.length ? `(${selectedRows.length})` : ''}
          </Button>
          <Button
            variant='outlined'
            color='secondary'
            sx={{
              backgroundColor: colors.primary[5],
            }}
            onClick={handleOnCancelAll}
          >
            Cancel
          </Button>
        </ActionButtonStack>
      ) : null}
    </ActionButtonWrapper>
  );
});
